import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import Reveal from "../components/Reveal";
import { ArrowRight, ArrowUpRight, Database, BarChart3, Layers, Cpu, LineChart, Workflow, Check } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/526565b4-e057-4988-9471-d86618ed67f7/images/33b08f96bbd9a47c0b38203affa535f3ecf797e6764b622f372d1833607e7bc1.png";
const DASH_IMG = "https://static.prod-images.emergentagent.com/jobs/526565b4-e057-4988-9471-d86618ed67f7/images/cee9b6d2ea4b8bcb9f0d8f90c19b53e02803de6a9684dd242a260c5c662fe75b.png";
const INFRA_IMG = "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg";
const CASES_BG = "https://static.prod-images.emergentagent.com/jobs/526565b4-e057-4988-9471-d86618ed67f7/images/f07d7395de43f46ff210e7dc4e24d339fddec838a2d2c3f95ac0ef9f56e10ad4.png";

const ICONS = [Database, BarChart3, Layers, Cpu, LineChart, Workflow];

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`max-w-7xl mx-auto px-6 md:px-10 lg:px-16 ${className}`}>
    {children}
  </section>
);

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#00B3B3]/90 uppercase">
    <span className="h-px w-8 bg-[#00B3B3]/60" />
    {children}
  </div>
);

export default function Home() {
  const { t } = useI18n();
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/40 via-black/70 to-[#0A0A0A]" />
        <div className="absolute inset-0 -z-10 grid-pattern opacity-40" />
        <Section className="pt-20 pb-24 md:pt-32 md:pb-40 relative">
          <Reveal>
            <Eyebrow>{t.hero.tag}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-5xl">
              {t.hero.title_a}
              <br />
              <span className="text-[#00B3B3] ">{t.hero.title_b}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.hero.sub}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#00B3B3] text-white font-semibold px-6 py-3 rounded-sm hover:bg-[#00CCCC] transition-colors"
              >
                {t.hero.cta_primary} <ArrowRight size={16} />
              </Link>
              <Link
                to="/solutions"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-sm hover:border-[#00B3B3] hover:text-[#00B3B3] transition-colors"
              >
                {t.hero.cta_secondary}
              </Link>
            </div>
          </Reveal>

          {/* Stats strip */}
          <Reveal delay={0.4}>
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {t.hero.stats.map((s, i) => (
                <div key={i} className="bg-[#0A0A0A] px-6 py-6 hover:bg-[#111] transition-colors">
                  <div className="font-heading font-black text-3xl md:text-4xl text-white tracking-tight">{s.value}</div>
                  <div className="font-mono text-[10px] tracking-widest text-white/40 uppercase mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </Section>
      </section>

      {/* SERVICES */}
      <Section className="py-24 md:py-32">
        <Reveal>
          <Eyebrow>{t.services.eyebrow}</Eyebrow>
        </Reveal>
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal delay={0.1}>
            <h2 className="font-heading font-bold tracking-tight text-white text-3xl md:text-5xl max-w-2xl">
              {t.services.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-white/60 max-w-md text-sm md:text-base">{t.services.sub}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group relative bg-[#0a0a0a] hover:bg-[#111] transition-colors p-8 h-full flex flex-col"
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-xs text-white/30 tracking-widest">{s.code}</span>
                    <Icon size={20} className="text-[#00B3B3]/70 group-hover:text-[#00B3B3] transition-colors" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white">{s.title}</h3>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.tags.map((tag) => (
                      <span key={tag} className="font-mono text-[10px] tracking-widest text-white/50 border border-white/10 px-2 py-1">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="absolute top-0 left-0 h-px w-0 bg-[#00B3B3] group-hover:w-full transition-all duration-500" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* METHODOLOGY */}
      <section className="relative">
        <div className="absolute inset-0 -z-10 grid-pattern opacity-30" />
        <Section className="py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <Reveal><Eyebrow>{t.method.eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-heading font-bold tracking-tight text-white text-3xl md:text-5xl">
                  {t.method.title}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 relative h-64 md:h-80 rounded-sm overflow-hidden border border-white/10">
                  <img src={INFRA_IMG} alt="infrastructure" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7 space-y-px bg-white/10 border border-white/10">
              {t.method.steps.map((step, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="bg-[#0a0a0a] p-8 hover:bg-[#111] transition-colors">
                    <div className="flex items-start gap-6">
                      <span className="font-mono text-[#00B3B3] text-sm tracking-widest pt-1">{step.n}</span>
                      <div>
                        <h4 className="font-heading font-semibold text-white text-xl">{step.t}</h4>
                        <p className="mt-2 text-white/60 text-sm leading-relaxed">{step.d}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* CASES */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{ backgroundImage: `url(${CASES_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0A0A0A] via-black/80 to-[#0A0A0A]" />
        <Section className="py-24 md:py-32">
          <Reveal><Eyebrow>{t.cases.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-white text-3xl md:text-5xl">
              {t.cases.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.cases.items.map((c, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`case-card-${i}`} className="bg-[#0c0c0c]/80 backdrop-blur border border-white/10 p-8 h-full hover:-translate-y-1 hover:border-[#00B3B3]/40 transition-all">
                  <div className="font-heading font-black text-5xl md:text-6xl text-[#39FF14] tracking-tighter">
                    {c.metric}
                  </div>
                  <div className="mt-3 font-mono text-xs text-white/50 tracking-widest uppercase">{c.label}</div>
                  <p className="mt-4 text-white/70 text-sm leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </section>

      {/* ABOUT preview */}
      <Section className="py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal><Eyebrow>{t.about.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading font-bold tracking-tight text-white text-3xl md:text-5xl">
                {t.about.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-white/70 leading-relaxed">{t.about.body}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <ul className="mt-8 space-y-3">
                {t.about.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                    <Check size={16} className="text-[#00B3B3] mt-1 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative border border-white/10 overflow-hidden rounded-sm">
              <img src={DASH_IMG} alt="dashboard preview" className="w-full h-auto" />
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-widest text-white/70 bg-black/50 backdrop-blur px-2 py-1 border border-white/10">
                SAC · LIVE
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* CTA STRIP */}
      <Section className="pb-24">
        <Reveal>
          <div className="relative border border-white/10 bg-gradient-to-br from-[#0c0c0c] via-[#0a0a0a] to-[#000] p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-20 -right-20 h-60 w-60 bg-[#00B3B3]/10 blur-3xl rounded-full" />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <Eyebrow>{t.contact.eyebrow}</Eyebrow>
                <h3 className="mt-4 font-heading font-bold text-white text-2xl md:text-4xl tracking-tight">
                  {t.contact.title}
                </h3>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="inline-flex items-center gap-2 bg-[#00B3B3] text-white font-semibold px-6 py-3 rounded-sm hover:bg-[#00CCCC] transition-colors"
                >
                  {t.nav.cta} <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
